import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, map, of, take } from 'rxjs';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { getToggleEntities } from 'Src/ng2/store/selectors/toggles-selectors';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';
import { TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';

// Sends users from a legacy network route to `home` for the same network entity when One Portal is
// enabled for that network context. It sits on the cluster routes, so it covers every network page
// under them, not just the dashboard. When OP is off (or required context is missing), returns
// `true` so the legacy network route is allowed to render — returning `false` would block the route
// entirely with no fallback.
//
// OpNetworkEvaluationGuard evaluates the formula for this network context on the parent `network`
// route, so this reads that evaluation rather than re-running the formula.
@Injectable({ providedIn: 'root' })
export class OpNetworkHomeRedirectGuard implements CanActivate {
  constructor (
    private router: Router,
    private store: Store<any>,
    private urlPathService: UrlPathService,
  ) {}

  canActivate (snapshot: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    // The network route stores entity type under `data.clusterType` (distinct from
    // `data.contextPartnerType`), so it's read directly here rather than via
    // UrlNavigationService.getEntityType. networkId can use the standard helper.
    const entityType = snapshot.data?.clusterType as TOpEntityType;
    const networkId = UrlNavigationService.getNetworkId(snapshot);
    if (!entityType || !networkId) {
      return of(true);
    }

    return this.store.select(getToggleEntities).pipe(
      take(1),
      map(toggles => {
        if (toggles?.[Toggles.EVALUATION_ONE_PORTAL_ENABLED] !== true) {
          return true;
        }

        const portalUrl = this.urlPathService.getPortalUrl({ entityType, networkId });
        return this.router.createUrlTree([`${portalUrl}/home`]);
      }),
    );
  }
}
